
@if (detail(); as item) {
  <div class="page-header">
    <div>
      <h1>{{ item.driver.displayName }}</h1>
      <p>Fahrerakte · {{ item.driver.email }}</p>
    </div>
  </div>

  @if (notice()) {
    <p class="info-message">{{ notice() }}</p>
  }

  <section class="panel driver-file-status">
    <div>
      <h2>Fahrerstatus</h2>

      <div class="driver-status-metrics">
        <article>
          <span>Zugang</span>
          <strong>{{ item.driver.accessActive ? 'Aktiv' : 'Inaktiv' }}</strong>
          <small>{{ item.driver.accessActive ? 'Portalzugang des Fahrers' : 'Login deaktiviert' }}</small>
        </article>

        <article>
          <span>Letzter Login</span>
          <strong>{{ formatLastLogin(item.driver.lastLoginAt) }}</strong>
          <small>{{ item.driver.lastLoginAt ? 'Letzte erfolgreiche Anmeldung' : 'Fahrer hat sich noch nicht angemeldet' }}</small>
        </article>

        <article>
          <span>Fortschritt</span>
          <strong>{{ item.stats.totalProgress }} %</strong>
          <small>über alle zugewiesenen Module</small>
        </article>

        <article>
          <span>Abgeschlossen</span>
          <strong>{{ item.stats.completedLessons }}</strong>
          <small>von {{ item.stats.totalLessons }} Lektionen</small>
        </article>

        <article>
          <span>Offen</span>
          <strong>{{ item.stats.openLessons }}</strong>
          <small>Lektionen noch zu erledigen</small>
        </article>
      </div>

      <div class="progress-track status-progress" aria-hidden="true">
        <div class="progress-fill" [style.width.%]="item.stats.totalProgress"></div>
      </div>

      @if (item.warning) {
        <div class="inline-warning" [class.danger]="item.warning.level === 'danger'" [class.warning]="item.warning.level === 'warning'">
          <h3>{{ item.warning.title }}</h3>
          <p>{{ item.warning.message }}</p>
          <strong>{{ item.warning.action }}</strong>
        </div>
      }
    </div>

    <aside class="driver-file-actions">
      <button type="button" class="primary-button" (click)="sendInvitation(item)">Einladung senden</button>
      <button type="button" class="primary-button" (click)="requestPasswordLink(item)">Passwort-Link senden</button>
      <button type="button" class="primary-button" (click)="sendReminder(item)">Fahrer anstupsen</button>
      <button type="button" class="primary-button" (click)="openAssignmentDialog(false)">Schulung zuweisen</button>
      <button type="button" class="primary-button" (click)="openAssignmentDialog(true)">Nachschulung zuweisen</button>
      <a class="ghost-button as-link" [routerLink]="['/drivers', item.driver.id, 'edit']">Fahrer bearbeiten</a>
      <a class="ghost-button as-link" routerLink="/drivers">Zurück zur Fahrerübersicht</a>
      @if (canDelete()) {
        <button type="button" class="ghost-button danger-button" (click)="deleteDriver(item)">Fahrer löschen</button>
      }
    </aside>
  </section>

  <details class="panel file-section" open>
    <summary class="file-section-summary">
      <span>Zugewiesene Module & Scoring</span>
    </summary>
    <p>Fortschritt zeigt, was erledigt wurde. Scoring zeigt, wie gut die Inhalte verstanden wurden. Details können pro Modul aufgeklappt werden.</p>

    <div class="module-score-list">
      @for (enrollment of item.enrollments; track enrollment.id) {
        <article class="module-score-row">
          <div>
            <span>Modul {{ enrollment.moduleNumber }}</span>
            <strong>{{ enrollment.courseTitle }}</strong>
            <small>{{ enrollmentStatusLabel(enrollment.status) }}</small>
          </div>

          <div>
            <span>Fortschritt</span>
            <strong>{{ enrollment.progressPercent }} %</strong>
          </div>

          <div>
            <span>Ø Lektionsquiz</span>
            <strong>{{ scoreOrDash(enrollment.averageLessonQuizScore) }}</strong>
          </div>

          <div>
            <span>Abschluss</span>
            <strong>{{ scoreOrDash(enrollment.finalScore) }}</strong>
          </div>

          <div>
            <span>Fehlversuche</span>
            @if (enrollment.failedAttempts > 0) {
              <a
                class="failed-attempt-link"
                [routerLink]="['/drivers', item.driver.id, 'evaluations', enrollment.id]"
                [attr.aria-label]="enrollment.failedAttempts + ' Fehlversuche im Modulbericht öffnen'"
              >
                {{ enrollment.failedAttempts }} Fehlversuche
                <span aria-hidden="true">→</span>
              </a>
            } @else {
              <strong>0 Fehlversuche</strong>
            }
          </div>
        </article>

        @if (enrollment.failedAttempts > 0) {
          <div class="lesson-attempt-list">
            @for (lesson of enrollment.lessons; track lesson.id) {
              @if (lesson.failedAttempts > 0) {
                <a
                  [routerLink]="['/drivers', item.driver.id, 'evaluations', enrollment.id]"
                  [queryParams]="{ lessonId: lesson.id }"
                >
                  <span>Lektion {{ lesson.lessonOrder }}</span>
                  <strong>{{ lesson.title }}</strong>
                  <small>{{ lesson.failedAttempts }} {{ lesson.failedAttempts === 1 ? 'Fehlversuch' : 'Fehlversuche' }} →</small>
                </a>
              }
            }
            @if (enrollment.finalFailedAttempts > 0) {
              <a
                [routerLink]="['/drivers', item.driver.id, 'evaluations', enrollment.id]"
                [queryParams]="{ type: 'FINAL' }"
              >
                <span>Modulabschluss</span>
                <strong>Abschluss-Wissenscheck</strong>
                <small>{{ enrollment.finalFailedAttempts }} {{ enrollment.finalFailedAttempts === 1 ? 'Fehlversuch' : 'Fehlversuche' }} →</small>
              </a>
            }
          </div>
        }
      } @empty {
        <div class="empty">Dieser Fahrer hat aktuell keine Kurszuweisungen.</div>
      }
    </div>
  </details>

  <details class="panel file-section" open>
    <summary class="file-section-summary">
      <span>Nachschulungen & Auswertung</span>
    </summary>
    <p>Hier siehst du zugewiesene Nachschulungen, den Bearbeitungsstand je Lektion und das Ergebnis der Wiederholungsfragen.</p>
    @if (item.stats.trainingAssignmentCount) {
      <p>{{ item.stats.trainingAssignmentCount }} Nachschulung(en) vorhanden.</p>
    } @else {
      <p>Keine individuellen Nachschulungen vorhanden.</p>
    }
  </details>

  <details class="panel file-section">
    <summary class="file-section-summary">
      <span>Stammdaten</span>
    </summary>
    <div class="detail-grid">
      <div><span>Name</span><strong>{{ item.driver.displayName }}</strong></div>
      <div><span>E-Mail</span><strong>{{ item.driver.email }}</strong></div>
      <div><span>Telefon</span><strong>{{ valueOrDash(item.driver.phone) }}</strong></div>
      <div><span>Fahrernummer</span><strong>{{ valueOrDash(item.driver.personnelNumber) }}</strong></div>
      <div><span>Sprache</span><strong>{{ item.driver.preferredLanguage.toUpperCase() }}</strong></div>
      <div><span>Fahrerstatus</span><strong>{{ statusLabel(item.driver.status) }}</strong></div>
      <div><span>Fahrerstatus aktiv bis</span><strong>{{ valueOrDash(item.driver.statusValidUntil) }}</strong></div>
      <div><span>Führerschein gültig bis</span><strong>{{ valueOrDash(item.driver.licenseValidUntil) }}</strong></div>
      <div><span>P-Schein gültig bis</span><strong>{{ valueOrDash(item.driver.pScheinValidUntil) }}</strong></div>
    </div>
  </details>

  <details class="panel file-section">
    <summary class="file-section-summary">
      <span>Zugang</span>
    </summary>
    <div class="detail-grid">
      <div><span>Login-E-Mail</span><strong>{{ item.driver.email }}</strong></div>
      <div><span>Status</span><strong>{{ item.driver.accessActive ? 'Login möglich' : 'Login deaktiviert' }}</strong></div>
      <div><span>Ereignisse</span><strong>{{ item.stats.invitationCount }}</strong></div>
      <div><span>Letzter Login</span><strong>{{ formatLastLogin(item.driver.lastLoginAt) }}</strong></div>
    </div>
  </details>

  <details class="panel file-section">
    <summary class="file-section-summary">
      <span>Nachweise</span>
    </summary>
    <div class="detail-grid">
      <div><span>Führerschein gültig bis</span><strong>{{ valueOrDash(item.driver.licenseValidUntil) }}</strong></div>
      <div><span>P-Schein gültig bis</span><strong>{{ valueOrDash(item.driver.pScheinValidUntil) }}</strong></div>
      <div><span>Fahrerstatus aktiv bis</span><strong>{{ valueOrDash(item.driver.statusValidUntil) }}</strong></div>
      <div><span>Zertifikate</span><strong>{{ item.stats.certificateCount }}</strong></div>
      <div><span>Betrieb</span><strong>{{ item.driver.companyName }}</strong></div>
    </div>
  </details>

  @if (assignmentDialogOpen()) {
    <div class="modal-backdrop" role="presentation" (click)="closeAssignmentDialog()">
      <section
        class="modal-panel"
        [class.wide-modal]="assignmentRetraining()"
        [class.retraining-assignment-modal]="assignmentRetraining()"
        role="dialog"
        aria-modal="true"
        aria-labelledby="assignment-title"
        (click)="$event.stopPropagation()"
      >
        <div class="panel-heading">
          <div>
            <h2 id="assignment-title">{{ assignmentRetraining() ? 'Nachschulung zuweisen' : 'Schulung zuweisen' }}</h2>
            <p>{{ assignmentRetraining() ? 'Wähle die Lektionen aus, die der Fahrer wiederholen soll.' : 'Eine oder mehrere Schulungen für diesen Fahrer auswählen.' }}</p>
          </div>
          <button type="button" class="ghost-button" (click)="closeAssignmentDialog()">Schließen</button>
        </div>

        <form class="dialog-form" (submit)="assignCourses($event)">
          @if (assignmentRetraining()) {
            <div class="full-width retraining-module-list">
              @for (course of courses(); track course.id; let first = $first) {
                <details class="retraining-module" [open]="first">
                  <summary>
                    <span>Modul {{ course.moduleNumber }} · {{ course.title }}</span>
                    <small>{{ course.lessons.length }} {{ course.lessons.length === 1 ? 'Lektion' : 'Lektionen' }}</small>
                  </summary>
                  <div class="retraining-lessons">
                    <label class="retraining-select-all">
                      <input
                        type="checkbox"
                        [checked]="allLessonsSelected(course)"
                        [indeterminate]="someLessonsSelected(course)"
                        (change)="toggleAllLessons(course, $event)"
                      >
                      <span>Alle Lektionen in diesem Modul auswählen</span>
                    </label>
                    @for (lesson of course.lessons; track lesson.id) {
                      <label class="retraining-lesson-row">
                        <input
                          type="checkbox"
                          [checked]="selectedLessonIds().includes(lesson.id)"
                          (change)="toggleLesson(lesson, $event)"
                        >
                        <span>{{ course.moduleNumber }}.{{ lesson.lessonOrder }}</span>
                        <strong>{{ lesson.title }}</strong>
                      </label>
                    } @empty {
                      <p class="empty">In diesem Modul sind keine aktiven Lektionen vorhanden.</p>
                    }
                  </div>
                </details>
              } @empty {
                <p class="empty">Keine aktiven Module vorhanden.</p>
              }
            </div>
          } @else {
            <div class="full-width checkbox-list">
              @for (course of courses(); track course.id) {
                <label class="checkbox-row">
                  <input type="checkbox" [checked]="selectedCourseIds().includes(course.id)" (change)="toggleCourse(course.id, $event)">
                  <span>Modul {{ course.moduleNumber }} · {{ course.title }}</span>
                </label>
              } @empty {
                <p class="empty">Keine aktiven Kurse vorhanden.</p>
              }
            </div>
          }

          <label>
            <span>Fällig bis</span>
            <input type="date" [value]="assignmentDueDate()" (input)="updateDueDate($event)">
          </label>

          @if (assignmentRetraining()) {
            <label>
              <span>Grund</span>
              <select [value]="assignmentReason()" (change)="updateReason($event)">
                <option value="MANUAL">Manuell</option>
                <option value="LOW_SCORE">Niedriges Scoring</option>
                <option value="OVERDUE">Überfällig</option>
                <option value="INCIDENT">Vorfall</option>
                <option value="QUALITY_REVIEW">Qualitätsprüfung</option>
                <option value="DOCUMENT_EXPIRED">Dokument abgelaufen</option>
              </select>
            </label>
          }

          <label class="full-width">
            <span>Hinweis</span>
            <textarea rows="4" [value]="assignmentDescription()" (input)="updateDescription($event)"></textarea>
          </label>

          @if (assignmentError()) {
            <p class="field-error full-width">{{ assignmentError() }}</p>
          }

          <div class="dialog-actions full-width">
            <button type="submit" class="primary-button" [disabled]="assigning()">
              {{ assigning() ? 'Wird gespeichert...' : (assignmentRetraining() ? 'Nachschulung zuweisen' : 'Schulung zuweisen') }}
            </button>
            <button type="button" class="ghost-button" (click)="closeAssignmentDialog()">Abbrechen</button>
          </div>
        </form>
      </section>
    </div>
  }
} @else {
  <div class="empty">Fahrerakte wird geladen.</div>
}
  
